import * as React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { makeTranslator, translateFunc } from '../../utilities/translations';
import { Security, ResearchOrderStore, ForskarBeställningFieldStates } from '../../models';
import { getErrorMessageByFieldName, getErrorMessageByFieldNameAfterHitSummary } from '../../utilities/helpers';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import AllowedDataHandlersList from './allowedDataHandlersList';
import { FormFieldProps, FormField } from 'sos-common/js/commonTypes/formField.types';
import { ValidationResult } from 'sos-common/js/commonTypes/validation.types';
import { InputError } from 'sos-common/js/components/InputError/InputError';
import { hashCode } from 'sos-common/js/utils/hash';
export interface securityAndPrivacyInputProps extends FormFieldProps<Security> {
  Translate: translateFunc;
  validationResults: ValidationResult[];
  fullValidationResults: ValidationResult[];
  hasHitSummary?: boolean;
}
export interface securityAndPrivacyInputState extends FormField<Security> {
  touchedMap: Map<string, boolean>;
}

let ProtectedByPrivacyLawOptions = {
  Osl: '',
  Osf: '',
  Other: '',
};

const DEFAULT_STATE_VALUE = {} as Security;

class SecurityAndPrivacyInput extends React.Component<securityAndPrivacyInputProps, securityAndPrivacyInputState> {
  static defaultProps = {
    errorMessage: '',
    onChange: () => {},
  };

  private _latestValue: Security | undefined;
  private _errorId: string;

  constructor(props: any) {
    super(props);
    const { Translate, fieldName, value, isRequired, label } = props;
    if (props.value !== undefined) {
      this._latestValue = value;
    } else {
      this._latestValue = DEFAULT_STATE_VALUE;
    }

    this._errorId = hashCode(props.id || props.fieldName, 'error');

    ProtectedByPrivacyLawOptions.Osf = Translate('/orderFormPage/StatistikSekretess2');
    ProtectedByPrivacyLawOptions.Osl = Translate('/orderFormPage/StatistikSekretess');
    ProtectedByPrivacyLawOptions.Other = Translate('/orderFormPage/AnnatLagrum');

    this.state = {
      value: this._latestValue,
      fieldName,
      errorMessage: '',
      touchedMap: new Map(),
    };
  }
  private get _errorMessage(): string | undefined {
    let { errorMessage } = this.state;
    if (!errorMessage && this.props.errorMessage) {
      errorMessage = this.props.errorMessage;
    }

    return errorMessage;
  }

  onChangeField = (field: ForskarBeställningFieldStates) => {
    const path = field.fieldName.replace('CurrentResearchOrder.Security.', '').split('.');

    this.setState(
      (state) => {
        const newState = { ...state };
        const security = newState.value;
        // Changing the radio choices resets values
        if (
          field.fieldName === 'CurrentResearchOrder.Security.ProtectedByPrivacyLaw' ||
          field.fieldName === 'CurrentResearchOrder.Security.PrivacyLaw'
        ) {
          security.AllowedDataHandlers = null;
          security.OtherLaw = null;
          security.OsfLaw = null;
          security.PrivacyLaw = null;
          newState.touchedMap = new Map();
        }
        _.set(security, path, field.value);
        newState.value = { ...security };
        newState.touchedMap.set(field.fieldName, true);
        return newState;
      },
      () => {
        this.props.onChange(this.state);
      },
    );
  };

  renderSubSection = () => {
    const { Translate, validationResults, fullValidationResults, hasHitSummary } = this.props;
    const Security = this.state.value;
    const errorMessage = this._errorMessage;
    const errorAriaAttr = errorMessage ? { 'aria-invalid': true, 'aria-describedby': this._errorId } : null;
    const privacyLawErrorMessage =
      this.state.touchedMap.get('CurrentResearchOrder.Security.PrivacyLaw') || hasHitSummary
        ? getErrorMessageByFieldName(fullValidationResults, 'CurrentResearchOrder.Security.PrivacyLaw')
        : '';
    return (
      <div className="indent" role="group" aria-labelledby="vilketLagrumLabel">
        <div id="vilketLagrumLabel" className="like-a-label">
          {Translate('/orderFormpage/VilketLagrum')}
        </div>

        <RadioButton<string>
          value={ProtectedByPrivacyLawOptions.Osf}
          checked={Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Osf}
          onChange={this.onChangeField}
          fieldName="CurrentResearchOrder.Security.PrivacyLaw"
          label={Translate('/orderFormPage/StatistikSekretess2')}
          errorAriaAttributes={errorAriaAttr}
          isRequired={true}
        />

        {Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Osf && (
          <div className="indent">
            <div className="form-group-small">
              <TextInput
                multiline
                classNames="height240px"
                fieldName="CurrentResearchOrder.Security.OsfLaw"
                placeholder={Translate('/orderFormpage/StatistikSekretess2Uppfylls')}
                maxLength={4000}
                value={_.get(Security, 'OsfLaw')}
                label={Translate('/orderFormpage/StatistikSekretess2Beskrivning')}
                onChange={this.onChangeField}
                errorMessage={
                  this.state.touchedMap.get('CurrentResearchOrder.Security.OsfLaw') || hasHitSummary
                    ? getErrorMessageByFieldName(fullValidationResults, 'CurrentResearchOrder.Security.OsfLaw')
                    : ''
                }
                isRequired={true}
              />
            </div>
          </div>
        )}
        <RadioButton<string>
          value={ProtectedByPrivacyLawOptions.Osl}
          checked={Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Osl}
          onChange={this.onChangeField}
          fieldName="CurrentResearchOrder.Security.PrivacyLaw"
          label={Translate('/orderFormPage/StatistikSekretess')}
          errorAriaAttributes={errorAriaAttr}
          isRequired={true}
        />

        {Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Osl && (
          <div className="indent">
            <div className="form-group-small">
              <span className="like-a-label">{Translate('/orderFormPage/StatistikSekretessBeskrivning')}</span>
            </div>
          </div>
        )}

        <RadioButton<string>
          value={ProtectedByPrivacyLawOptions.Other}
          checked={Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Other}
          onChange={this.onChangeField}
          fieldName="CurrentResearchOrder.Security.PrivacyLaw"
          label={Translate('/orderFormPage/AnnatLagrum')}
          errorAriaAttributes={errorAriaAttr}
          isRequired={true}
        />

        {Security.PrivacyLaw === ProtectedByPrivacyLawOptions.Other && (
          <div className="indent">
            <div className="form-group-small">
              <TextInput
                fieldName="CurrentResearchOrder.Security.OtherLaw"
                placeholder={Translate('/orderFormpage/Lagrum')}
                maxLength={160}
                value={_.get(Security, 'OtherLaw')}
                label={Translate('/orderFormpage/VilketLagrum')}
                onChange={this.onChangeField}
                errorMessage={
                  this.state.touchedMap.get('CurrentResearchOrder.Security.OtherLaw') || hasHitSummary
                    ? getErrorMessageByFieldName(fullValidationResults, 'CurrentResearchOrder.Security.OtherLaw')
                    : ''
                }
                isRequired={true}
              />
            </div>
          </div>
        )}

        {privacyLawErrorMessage && <InputError id={this._errorId} errorText={privacyLawErrorMessage} />}
      </div>
    );
  };

  public render() {
    const { Translate, validationResults, fullValidationResults } = this.props;
    const Security = this.state.value;
    const { ProtectedByPrivacyLaw } = Security;
    const errorMessage = this._errorMessage;
    const errorAriaAttr = errorMessage ? { 'aria-invalid': true, 'aria-describedby': this._errorId } : null;
    return (
      <div role="group" aria-labelledby="bestallDataOffentlighetsLabel">
        <h2 id="bestallDataOffentlighetsLabel" className="inlineblock">
          {Translate('/orderFormpage/bestallDataOffentlighets')}
        </h2>
        <p>{Translate('/orderFormpage/KontaktaForetradare')}</p>
        <RadioButton
          fieldName="CurrentResearchOrder.Security.ProtectedByPrivacyLaw"
          label={Translate('/orderFormPage/svar1')}
          value={true}
          checked={Security.ProtectedByPrivacyLaw === true}
          onChange={this.onChangeField}
          errorAriaAttributes={errorAriaAttr}
          isRequired={true}
        />
        {ProtectedByPrivacyLaw && this.renderSubSection()}
        <RadioButton
          fieldName="CurrentResearchOrder.Security.ProtectedByPrivacyLaw"
          label={Translate('/orderFormPage/NejUppgifterSyddasInte')}
          value={false}
          checked={Security.ProtectedByPrivacyLaw === false}
          onChange={this.onChangeField}
          errorAriaAttributes={errorAriaAttr}
          isRequired={true}
        />
        {ProtectedByPrivacyLaw === false && (
          <div className="indent">
            <h3>{Translate('/orderFormPage/LaggTillAllaPersoner')}</h3>
            <AllowedDataHandlersList
              value={_.get(Security, 'AllowedDataHandlers')}
              namePlaceholder={Translate('/orderFormpage/fnamnOchEftn')}
              organisationPlaceholder={Translate('/orderFormpage/OrganisationAllowedDataHandlers')}
              fieldName="CurrentResearchOrder.Security.AllowedDataHandlers"
              // Sending fullValidationResults here and implementing custom Touched-logic for allowedDataHandlerItem
              validationResults={fullValidationResults}
              onChange={this.onChangeField}
              hasHitSummary={this.props.hasHitSummary}
              errorMessage={
                this.state.touchedMap.get('CurrentResearchOrder.Security.AllowedDataHandlers') ||
                this.props.hasHitSummary
                  ? getErrorMessageByFieldName(
                      fullValidationResults,
                      'CurrentResearchOrder.Security.AllowedDataHandlers',
                    )
                  : ''
              }
            />
            <br />
            <TextInput
              fieldName="CurrentResearchOrder.Security.PdfOnly.NarArProjektetSlutfort"
              placeholder={Translate('/orderFormPage/NarArProjektetSlutfort_Datum')}
              label={Translate('/orderFormPage/NarArProjektetSlutfort')}
              onChange={this.onChangeField}
              maxLength={160}
              value={Security.PdfOnly.NarArProjektetSlutfort}
              isRequired={true}
              errorMessage={
                this.state.touchedMap.get('CurrentResearchOrder.Security.PdfOnly.NarArProjektetSlutfort') ||
                this.props.hasHitSummary
                  ? getErrorMessageByFieldName(
                      fullValidationResults,
                      'CurrentResearchOrder.Security.PdfOnly.NarArProjektetSlutfort',
                    )
                  : ''
              }
            />
          </div>
        )}
        {errorMessage && <InputError id={this._errorId} errorText={errorMessage} />}
      </div>
    );
  }
}

const mapState2Props = (state: ResearchOrderStore) => {
  return {
    Translate: makeTranslator(state.globals.translations),
    value: state.formModel.Security,
  };
};

export default connect(mapState2Props)(SecurityAndPrivacyInput);
